import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Program, ProgramCategory } from '@/lib/api';
import type { CreateProgramPayload, UpdateProgramPayload } from '@/lib/api';

interface ProgramFormProps {
  categories: ProgramCategory[];
  initial?: Program | null;
  onSave: (payload: CreateProgramPayload | UpdateProgramPayload) => Promise<void>;
  onCancel: () => void;
}

export function ProgramForm({ categories, initial, onSave, onCancel }: ProgramFormProps) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [categoryId, setCategoryId] = useState(initial?.category_id ?? categories[0]?.id ?? '');
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (initial) {
        await onSave({ title, slug, sortOrder, ...(categoryId ? { categoryId } : {}) } as UpdateProgramPayload);
      } else {
        await onSave({ categoryId, slug, title, sortOrder });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Category</label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          required
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Title</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Slug (URL)</label>
        <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. my-program" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Sort order</label>
        <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value) || 0)} />
      </div>
      <div className="flex gap-2 pt-4">
        <Button type="submit" disabled={saving} className="bg-accent text-accent-foreground hover:bg-gold-light">
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
